/**
 * Leighton Parts product page: gallery thumbs, quantity stepper, variant selection and the
 * mobile sticky buy bar.
 *
 * All four are enhancements. The gallery's hero is a real image and the thumbs are real
 * buttons; the quantity field is a real number input; the variant selects post a real form.
 * With this file absent the page still sells the product, it just does a page load per
 * variant change.
 */

class LpProduct extends HTMLElement {
  #variants = [];
  #observer = null;
  /** In-flight variant re-render, aborted when a newer selection supersedes it. */
  #abortController = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('input', this.#onInput);

    this.#variants = this.#readVariants();
    this.#watchSubmitVisibility();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#abortController?.abort();
  }

  /**
   * Variant data comes from a JSON script tag the section renders, not from scraping the
   * DOM: option labels are merchant text and can contain anything, including the separator
   * any string-matching approach would need.
   */
  #readVariants() {
    const node = this.querySelector('[data-lp-variants]');
    if (!node?.textContent) return [];
    try {
      return JSON.parse(node.textContent);
    } catch {
      return [];
    }
  }

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const thumb = target.closest('[data-lp-thumb]');
    if (thumb instanceof HTMLElement) {
      this.#selectThumb(thumb);
      return;
    }

    const step = target.closest('[data-lp-qty-step]');
    if (step instanceof HTMLElement) {
      this.#stepQuantity(Number(step.dataset.lpQtyStep));
      return;
    }

    if (target.closest('[data-lp-sticky-submit]')) {
      // Delegates to the real button so there is one submit path, one disabled state and
      // one place where add-to-cart behaviour lives.
      const submit = this.querySelector('[data-lp-submit]');
      if (submit instanceof HTMLButtonElement && !submit.disabled) submit.click();
    }
  };

  #onChange = (event) => {
    const target = event.target;
    if (target instanceof HTMLSelectElement && target.matches('[data-lp-option-index]')) {
      this.#selectVariant();
    }
  };

  #onInput = (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.matches('[data-lp-qty-input]')) {
      this.#clampQuantity(target);
    }
  };

  /**
   * @param {HTMLElement} thumb
   */
  #selectThumb(thumb) {
    const hero = this.querySelector('.lp-gallery__image');
    if (!(hero instanceof HTMLImageElement)) return;

    const src = thumb.dataset.mediaSrc;
    if (!src) return;

    // srcset would otherwise keep winning over the new src and the hero would never change.
    hero.removeAttribute('srcset');
    hero.src = src;
    hero.alt = thumb.dataset.mediaAlt || '';

    for (const other of this.querySelectorAll('[data-lp-thumb]')) {
      const isActive = other === thumb;
      other.classList.toggle('lp-gallery__thumb--active', isActive);
      if (isActive) {
        other.setAttribute('aria-current', 'true');
      } else {
        other.removeAttribute('aria-current');
      }
    }
  }

  /**
   * @param {number} delta
   */
  #stepQuantity(delta) {
    const input = this.querySelector('[data-lp-qty-input]');
    if (!(input instanceof HTMLInputElement)) return;
    input.value = String(Math.max(1, (Number(input.value) || 1) + delta));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * @param {HTMLInputElement} input
   */
  #clampQuantity(input) {
    // Only clamps a value that is present and invalid. An empty field is left alone while
    // typing, otherwise clearing it to type "12" instantly rewrites it to "1".
    if (input.value === '') return;
    const value = Number(input.value);
    if (!Number.isFinite(value) || value < 1) input.value = '1';
  }

  #selectVariant() {
    const selects = [...this.querySelectorAll('[data-lp-option-index]')];
    const chosen = selects.map((select) => /** @type {HTMLSelectElement} */ (select).value);

    const match = this.#variants.find(
      (variant) => Array.isArray(variant.options) && variant.options.every((option, i) => option === chosen[i])
    );

    const idField = this.querySelector('[data-lp-variant-id]');
    // A part that is supplied and fitted only has no add to basket, but its price and photo
    // still follow the selects.
    const submit = this.querySelector('[data-lp-submit]');
    const buyable = submit instanceof HTMLButtonElement;

    if (!match) {
      if (buyable) {
        submit.disabled = true;
        submit.textContent = submit.dataset.unavailableLabel || 'Unavailable';
      }
      return;
    }

    if (idField instanceof HTMLInputElement) idField.value = String(match.id);

    if (buyable) {
      submit.disabled = !match.available;
      submit.textContent = match.available
        ? submit.dataset.addLabel || 'Add to cart'
        : submit.dataset.notifyLabel || 'Notify me';
    }

    // The URL carries the variant so the page is shareable and a reload keeps the choice.
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(match.id));
    window.history.replaceState({}, '', url);

    // Everything above is instant and local. Price, stock and image come from the server.
    this.#renderVariant(match.id, url);
  }

  /**
   * Re-renders the variant-dependent parts of the page through the Section Rendering API.
   *
   * Price is NOT recomputed in JS. lp-price.liquid derives ex-VAT from a rate that can be
   * overridden per product by a metafield, formats through `money`, and decides the
   * compare-at and SAVE chip — reimplementing any of that here is exactly the drift that
   * snippet exists to prevent. Asking the server for the rendered markup keeps one formatter.
   *
   * The alternative, embedding pre-rendered price HTML for every variant, was measured and
   * rejected: the largest wheelsets carry 96 variants.
   *
   * @param {number|string} variantId
   * @param {URL} url - The canonical URL for this variant, used as the fallback navigation.
   */
  async #renderVariant(variantId, url) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;

    const endpoint = new URL(window.location.pathname, window.location.origin);
    endpoint.searchParams.set('variant', String(variantId));
    endpoint.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(endpoint, { signal: controller.signal });
      if (!response.ok) throw new Error(`Section render failed: ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      this.#replaceContent(doc, '[data-lp-price]');
      this.#replaceContent(doc, '[data-lp-sticky-price]');
      this.#replaceContent(doc, '[data-lp-stock]');
      this.#replaceHero(doc);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      // A stale price next to a working add-to-basket is the one failure worth a page load:
      // the customer would read one price and cart another. Fall back to the no-JS path,
      // which is a full navigation to the variant URL.
      window.location.assign(url.toString());
    }
  }

  /**
   * @param {Document} doc
   * @param {string} selector
   */
  #replaceContent(doc, selector) {
    const next = doc.querySelector(selector);
    const current = this.querySelector(selector);
    if (!next || !current) return;
    current.innerHTML = next.innerHTML;
  }

  /**
   * Swaps the gallery hero only when the variant actually resolves to a different image.
   * Most variants share the product's media, and reassigning an identical src would throw
   * away a decoded image and flash the tile for nothing.
   *
   * @param {Document} doc
   */
  #replaceHero(doc) {
    const next = doc.querySelector('.lp-gallery__image');
    const current = this.querySelector('.lp-gallery__image');
    if (!(next instanceof HTMLImageElement) || !(current instanceof HTMLImageElement)) return;
    if (LpProduct.#assetPath(next.src) === LpProduct.#assetPath(current.src)) return;

    current.removeAttribute('srcset');
    current.src = next.src;
    current.alt = next.alt;

    // Keep the thumb strip's active state honest about which image is showing. Matched on
    // the CDN path, not the whole URL: the thumb's data-media-src is rendered at width 1400
    // while the hero's src is whatever the browser picked out of its srcset, so the two are
    // the same picture with different query strings and never compare equal as strings.
    const activePath = LpProduct.#assetPath(next.src);
    for (const thumb of this.querySelectorAll('[data-lp-thumb]')) {
      if (!(thumb instanceof HTMLElement)) continue;
      const isActive = LpProduct.#assetPath(thumb.dataset.mediaSrc) === activePath;
      thumb.classList.toggle('lp-gallery__thumb--active', isActive);
      if (isActive) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    }
  }

  /**
   * The path part of a Shopify CDN image URL, which identifies the picture independently of
   * the width, format and version parameters appended to it.
   *
   * @param {string|undefined} src
   * @returns {string}
   */
  static #assetPath(src) {
    if (!src) return '';
    try {
      return new URL(src, window.location.origin).pathname;
    } catch {
      return src;
    }
  }

  /**
   * Reveals the sticky bar only once the real add-to-basket is off screen. An observer on
   * the button itself is used rather than a scroll offset so it stays correct whatever the
   * buy column's height turns out to be for a given product.
   */
  #watchSubmitVisibility() {
    const sticky = this.querySelector('[data-lp-sticky]');
    const submit = this.querySelector('[data-lp-submit]');
    if (!(sticky instanceof HTMLElement) || !(submit instanceof HTMLElement)) return;
    if (!('IntersectionObserver' in window)) return;

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        sticky.hidden = entry.isIntersecting;
      },
      { rootMargin: '0px 0px -80px 0px' }
    );
    this.#observer.observe(submit);
  }
}

if (!customElements.get('lp-product')) {
  customElements.define('lp-product', LpProduct);
}
